<script setup lang="ts">
import { ref } from 'vue'

const expandedSections = ref<Set<string>>(new Set(['access']))

function toggleSection(section: string) {
  if (expandedSections.value.has(section)) {
    expandedSections.value.delete(section)
  } else {
    expandedSections.value.add(section)
  }
}

function isExpanded(section: string): boolean {
  return expandedSections.value.has(section)
}
</script>

<template>
  <div class="max-w-3xl mx-auto p-4 md:p-6">
    <h1 class="text-2xl font-bold text-gray-800 mb-2">Know Before You Go</h1>
    <p class="text-gray-600 mb-8">Essential information for your Vermont cabin stay</p>

    <!-- Quick Facts -->
    <div class="card mb-6 bg-gradient-to-r from-primary-50 to-blue-50">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        <div>
          <div class="text-2xl mb-1">🏔️</div>
          <div class="text-sm text-gray-600">Elevation</div>
          <div class="font-bold">2,100 ft</div>
        </div>
        <div>
          <div class="text-2xl mb-1">🛏️</div>
          <div class="text-sm text-gray-600">Sleeps</div>
          <div class="font-bold">6 guests</div>
        </div>
        <div>
          <div class="text-2xl mb-1">📶</div>
          <div class="text-sm text-gray-600">Internet</div>
          <div class="font-bold">Starlink</div>
        </div>
        <div>
          <div class="text-2xl mb-1">⚡</div>
          <div class="text-sm text-gray-600">Power</div>
          <div class="font-bold">Solar + Generator</div>
        </div>
      </div>
    </div>

    <!-- Accordion Sections -->
    <div class="space-y-4">
      <!-- Access & Directions -->
      <div class="card">
        <button
          @click="toggleSection('access')"
          class="w-full flex items-center justify-between text-left"
        >
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span>🚗</span> Getting There
          </h2>
          <span class="text-2xl text-gray-400">{{ isExpanded('access') ? '−' : '+' }}</span>
        </button>
        <div v-if="isExpanded('access')" class="mt-4 space-y-4">
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p class="font-bold text-yellow-800">Vehicle Requirements</p>
            <ul class="text-yellow-700 text-sm mt-2 space-y-1">
              <li>• <strong>4x4 or AWD required</strong> year-round</li>
              <li>• <strong>10+ inches ground clearance</strong> recommended</li>
              <li>• <strong>Tire chains required</strong> November - April</li>
              <li>• The last 0.5 miles is a steep, unpaved mountain road</li>
            </ul>
          </div>
          <div>
            <p class="font-medium text-gray-800 mb-2">Directions from Rutland:</p>
            <ol class="text-gray-600 text-sm space-y-2 list-decimal list-inside">
              <li>Take US-4 East from Rutland toward Killington</li>
              <li>Turn left onto VT-100 North in West Bridgewater</li>
              <li>Continue north through Pittsfield (~15 miles)</li>
              <li>Turn left onto [Private Road] - look for the blue reflector</li>
              <li>Follow the gravel road 0.5 miles to the cabin</li>
            </ol>
          </div>
          <div>
            <p class="font-medium text-gray-800 mb-2">Directions from I-89:</p>
            <ol class="text-gray-600 text-sm space-y-2 list-decimal list-inside">
              <li>Take Exit 3 (Bethel/Royalton)</li>
              <li>Turn right onto VT-107 West toward Bethel</li>
              <li>Continue on VT-107 to Stockbridge (~12 miles)</li>
              <li>Turn left onto VT-100 South toward Pittsfield</li>
              <li>Turn right onto [Private Road] - look for the blue reflector</li>
              <li>Follow the gravel road 0.5 miles to the cabin</li>
            </ol>
          </div>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-sm text-blue-700">
              <strong>Pro tip:</strong> Download offline maps before you arrive.
              Cell service is limited in the mountains.
            </p>
          </div>
        </div>
      </div>

      <!-- What to Bring -->
      <div class="card">
        <button
          @click="toggleSection('packing')"
          class="w-full flex items-center justify-between text-left"
        >
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span>🎒</span> What to Bring
          </h2>
          <span class="text-2xl text-gray-400">{{ isExpanded('packing') ? '−' : '+' }}</span>
        </button>
        <div v-if="isExpanded('packing')" class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p class="font-medium text-gray-800 mb-2">Essentials</p>
              <ul class="text-gray-600 text-sm space-y-1">
                <li>✓ Food and groceries (no stores nearby)</li>
                <li>✓ Drinking water or water filter</li>
                <li>✓ Flashlight / headlamp</li>
                <li>✓ Phone charger</li>
                <li>✓ First aid kit</li>
                <li>✓ Cash (limited cell for payments)</li>
              </ul>
            </div>
            <div>
              <p class="font-medium text-gray-800 mb-2">Winter Additions (Nov-Apr)</p>
              <ul class="text-gray-600 text-sm space-y-1">
                <li>✓ Tire chains (MUST have)</li>
                <li>✓ Snow boots and warm layers</li>
                <li>✓ Extra blankets</li>
                <li>✓ Snow shovel in vehicle</li>
                <li>✓ Emergency supplies in car</li>
              </ul>
            </div>
          </div>
          <div class="mt-4">
            <p class="font-medium text-gray-800 mb-2">Provided at Cabin</p>
            <ul class="text-gray-600 text-sm space-y-1">
              <li>• Bedding and towels</li>
              <li>• Basic cookware and dishes</li>
              <li>• Coffee maker</li>
              <li>• Firewood (outdoor stack)</li>
              <li>• Board games and books</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Power & Heating -->
      <div class="card">
        <button
          @click="toggleSection('power')"
          class="w-full flex items-center justify-between text-left"
        >
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span>⚡</span> Power & Heating
          </h2>
          <span class="text-2xl text-gray-400">{{ isExpanded('power') ? '−' : '+' }}</span>
        </button>
        <div v-if="isExpanded('power')" class="mt-4 space-y-4">
          <div>
            <p class="font-medium text-gray-800 mb-2">Solar Power System</p>
            <p class="text-gray-600 text-sm">
              The cabin runs on solar power with battery backup. A propane generator
              provides backup power during extended cloudy periods. Normal usage
              (lights, devices, WiFi) is fine. Avoid running multiple high-draw
              appliances simultaneously.
            </p>
          </div>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="font-medium text-gray-800 mb-2">Power Tips</p>
            <ul class="text-gray-600 text-sm space-y-1">
              <li>• Turn off lights when leaving rooms</li>
              <li>• Unplug chargers when not in use</li>
              <li>• The generator starts automatically if needed</li>
              <li>• Check the dashboard for battery status</li>
            </ul>
          </div>
          <div>
            <p class="font-medium text-gray-800 mb-2">Radiant Floor Heating</p>
            <p class="text-gray-600 text-sm">
              The cabin has radiant floor heating controlled by a smart thermostat.
              Pre-heating is automatic based on your reservation. The system takes
              4-6 hours to warm up in cold weather due to the thermal mass of
              the concrete floors.
            </p>
          </div>
        </div>
      </div>

      <!-- House Rules -->
      <div class="card">
        <button
          @click="toggleSection('rules')"
          class="w-full flex items-center justify-between text-left"
        >
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span>📋</span> House Rules
          </h2>
          <span class="text-2xl text-gray-400">{{ isExpanded('rules') ? '−' : '+' }}</span>
        </button>
        <div v-if="isExpanded('rules')" class="mt-4">
          <ul class="space-y-3">
            <li class="flex gap-3">
              <span class="text-xl">🚭</span>
              <div>
                <p class="font-medium text-gray-800">No Smoking</p>
                <p class="text-sm text-gray-600">Smoking outside only, dispose of butts properly</p>
              </div>
            </li>
            <li class="flex gap-3">
              <span class="text-xl">🐕</span>
              <div>
                <p class="font-medium text-gray-800">Pets Welcome</p>
                <p class="text-sm text-gray-600">Well-behaved dogs allowed. Clean up after them.</p>
              </div>
            </li>
            <li class="flex gap-3">
              <span class="text-xl">🔥</span>
              <div>
                <p class="font-medium text-gray-800">Fire Safety</p>
                <p class="text-sm text-gray-600">Use the wood stove responsibly. Never leave fires unattended.</p>
              </div>
            </li>
            <li class="flex gap-3">
              <span class="text-xl">🐻</span>
              <div>
                <p class="font-medium text-gray-800">Wildlife</p>
                <p class="text-sm text-gray-600">Store food securely. Don't leave trash outside.</p>
              </div>
            </li>
            <li class="flex gap-3">
              <span class="text-xl">🧹</span>
              <div>
                <p class="font-medium text-gray-800">Check-out</p>
                <p class="text-sm text-gray-600">Strip beds, start dishwasher, take trash. Leave as you found it.</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Emergency Info -->
      <div class="card border-red-200">
        <button
          @click="toggleSection('emergency')"
          class="w-full flex items-center justify-between text-left"
        >
          <h2 class="text-lg font-bold text-red-800 flex items-center gap-2">
            <span>🚨</span> Emergency Information
          </h2>
          <span class="text-2xl text-gray-400">{{ isExpanded('emergency') ? '−' : '+' }}</span>
        </button>
        <div v-if="isExpanded('emergency')" class="mt-4 space-y-4">
          <div class="bg-red-50 rounded-lg p-4">
            <p class="font-bold text-red-800">Emergency: 911</p>
            <p class="text-sm text-red-700 mt-1">
              Be prepared to give the cabin address and GPS coordinates.
              Cell service may be weak - try texting if calls don't connect.
            </p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="font-medium text-gray-800 mb-2">Address</p>
              <p class="text-gray-600 text-sm">
                [Cabin Address]<br>
                Brandon, VT 05733
              </p>
            </div>
            <div>
              <p class="font-medium text-gray-800 mb-2">GPS Coordinates</p>
              <p class="text-gray-600 text-sm font-mono">
                43.XXXX° N, 72.XXXX° W
              </p>
            </div>
          </div>
          <div>
            <p class="font-medium text-gray-800 mb-2">Nearest Hospital</p>
            <p class="text-gray-600 text-sm">
              Rutland Regional Medical Center<br>
              160 Allen St, Rutland, VT 05701<br>
              (802) 775-7111 • ~30 minutes
            </p>
          </div>
          <div>
            <p class="font-medium text-gray-800 mb-2">Property Contact</p>
            <p class="text-gray-600 text-sm">
              Constantine: [Phone Number]<br>
              For non-emergencies or cabin questions
            </p>
          </div>
        </div>
      </div>

      <!-- FAQ -->
      <div class="card">
        <button
          @click="toggleSection('faq')"
          class="w-full flex items-center justify-between text-left"
        >
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span>❓</span> FAQ
          </h2>
          <span class="text-2xl text-gray-400">{{ isExpanded('faq') ? '−' : '+' }}</span>
        </button>
        <div v-if="isExpanded('faq')" class="mt-4 space-y-4">
          <div>
            <p class="font-medium text-gray-800">Is there WiFi?</p>
            <p class="text-gray-600 text-sm">
              Yes! Starlink satellite internet. Good for streaming and video calls.
              Network: "Vermont Cabin" - password provided at booking.
            </p>
          </div>
          <div>
            <p class="font-medium text-gray-800">Can I work remotely from the cabin?</p>
            <p class="text-gray-600 text-sm">
              Yes, the Starlink connection is reliable for remote work.
              There's a desk area with good lighting.
            </p>
          </div>
          <div>
            <p class="font-medium text-gray-800">What if I get stuck in snow?</p>
            <p class="text-gray-600 text-sm">
              With proper vehicle and chains, this is rare. If it happens,
              stay with your vehicle and call for help. Keep emergency supplies in your car.
            </p>
          </div>
          <div>
            <p class="font-medium text-gray-800">Is the water safe to drink?</p>
            <p class="text-gray-600 text-sm">
              The cabin has a well with filtration. We recommend bringing
              drinking water or using a filter for extra peace of mind.
            </p>
          </div>
          <div>
            <p class="font-medium text-gray-800">What if the power goes out?</p>
            <p class="text-gray-600 text-sm">
              The generator starts automatically. If there's an extended outage,
              flashlights are in the kitchen drawer. The wood stove provides backup heat.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="mt-8 text-center">
      <router-link to="/request" class="btn btn-primary">
        Book Your Stay
      </router-link>
      <p class="text-sm text-gray-500 mt-2">
        Questions? Contact us before booking.
      </p>
    </div>
  </div>
</template>
